import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Bell, AlertTriangle, Megaphone, CheckCircle2, X, ExternalLink, Volume2 } from "lucide-react";
import { useLiveAnalytics } from "@/context/LiveAnalyticsContext";

export function LiveNotificationCenter() {
  const { notifications, dismissNotification } = useLiveAnalytics();

  if (!notifications || notifications.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[99999] flex flex-col gap-3 max-w-sm sm:max-w-md w-full pointer-events-none px-3 sm:px-0"
      data-testid="live-notification-center"
      aria-live="polite"
    >
      {notifications.map((notif) => {
        const typeConfig = {
          announcement: {
            bg: "bg-zinc-950/95 border-[#E11D48]/50 shadow-rose-950/40",
            badgeBg: "bg-[#E11D48]/15 text-[#E11D48] border-[#E11D48]/30",
            icon: Megaphone,
            iconColor: "text-[#E11D48]",
            label: "Anuncio de La Caja LGBT"
          },
          urgent: {
            bg: "bg-zinc-950/95 border-rose-500/60 shadow-rose-950/50",
            badgeBg: "bg-rose-500/20 text-rose-400 border-rose-500/40",
            icon: AlertTriangle,
            iconColor: "text-rose-400 animate-pulse",
            label: "Aviso Urgente"
          },
          warning: {
            bg: "bg-zinc-950/95 border-amber-500/50 shadow-amber-950/40",
            badgeBg: "bg-amber-500/15 text-amber-400 border-amber-500/30",
            icon: AlertTriangle,
            iconColor: "text-amber-400",
            label: "Notificación de Servicio"
          },
          success: {
            bg: "bg-zinc-950/95 border-emerald-500/50 shadow-emerald-950/40",
            badgeBg: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
            icon: CheckCircle2,
            iconColor: "text-emerald-400",
            label: "Novedad Exclusiva"
          },
          info: {
            bg: "bg-zinc-950/95 border-sky-500/40 shadow-sky-950/30",
            badgeBg: "bg-sky-500/15 text-sky-400 border-sky-500/30",
            icon: Bell,
            iconColor: "text-sky-400",
            label: "Mensaje en Vivo"
          }
        }[notif.type] || {
          bg: "bg-zinc-950/95 border-zinc-700 shadow-zinc-950/50",
          badgeBg: "bg-zinc-800 text-zinc-300 border-zinc-700",
          icon: Bell,
          iconColor: "text-zinc-300",
          label: "Notificación"
        };

        const IconComponent = typeConfig.icon;

        return (
          <div
            key={notif.id}
            className={`pointer-events-auto relative overflow-hidden rounded-2xl border ${typeConfig.bg} backdrop-blur-xl shadow-2xl p-4 sm:p-5 transition-all duration-300 transform translate-y-0 animate-in fade-in slide-in-from-top-4`}
          >
            {/* Header / Badge */}
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${typeConfig.badgeBg}`}>
                  <IconComponent className={`w-3.5 h-3.5 ${typeConfig.iconColor}`} />
                  <span>{typeConfig.label}</span>
                </span>
                {notif.playSound && (
                  <Volume2 className="w-3 h-3 text-zinc-500" title="Alerta sonora emitida" />
                )}
              </div>

              <button
                onClick={() => dismissNotification(notif.id)}
                className="p-1 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800/60 transition-colors"
                aria-label="Cerrar notificación"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Title & Body */}
            <div className="space-y-1.5 pr-2">
              {notif.title && (
                <h4 className="font-heading text-sm sm:text-base font-bold text-white leading-snug">
                  {notif.title}
                </h4>
              )}
              {notif.message && (
                <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {notif.message}
                </p>
              )}
            </div>

            {/* Action button if provided */}
            {notif.actionText && notif.actionUrl && (
              <div className="mt-3 pt-2.5 border-t border-zinc-800/80 flex items-center justify-end">
                {notif.actionUrl.startsWith("http") ? (
                  <a
                    href={notif.actionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => dismissNotification(notif.id)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-semibold shadow-lg shadow-rose-950/40 transition-colors"
                  >
                    <span>{notif.actionText}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <Link
                    to={notif.actionUrl}
                    onClick={() => dismissNotification(notif.id)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#E11D48] hover:bg-[#BE123C] text-white text-xs font-semibold shadow-lg shadow-rose-950/40 transition-colors"
                  >
                    <span>{notif.actionText}</span>
                    <Sparkles className="w-3 h-3" />
                  </Link>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
